import { useCallback, useRef, useState } from 'react';

import { ancanaApi } from '../../../api/ancanaApi';
import { errorMessage } from '../tenancy/useOperators';
import { IDriveSearchResult } from '../../../types/models/pathfinder';

const PATH = 'pathfinder/search/';

export interface UseDriveSearchResult {
  result?: IDriveSearchResult;
  isLoading: boolean;
  error?: string;
  isForbidden: boolean;
  /** The term the current `result` belongs to — empty before the first search. */
  term: string;
  search: (term: string, options?: { type?: string; rootKey?: string }) => Promise<void>;
  clear: () => void;
}

/**
 * Search the Drive index.
 *
 * Unlike `useIndexHealth`, this does **not** poll. A search is a question a
 * person asked; re-asking it every minute would spend Pathfinder round trips
 * to change an answer nobody is looking at, and would move the grid under a
 * cursor mid-click.
 *
 * Out-of-order responses are dropped rather than rendered. Someone typing
 * "planta" fires several searches whose replies can land in any order, and the
 * reply to "plan" arriving last would silently replace correct results with
 * stale ones — the bug reads as "search is wrong", not as "search is racy".
 */
const useDriveSearch = (): UseDriveSearchResult => {
  const [result, setResult] = useState<IDriveSearchResult>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isForbidden, setIsForbidden] = useState(false);
  const [term, setTerm] = useState('');
  const latest = useRef(0);

  const search = useCallback(async (raw: string, options?: { type?: string; rootKey?: string }) => {
    const query = raw.trim();
    if (!query) return;

    const ticket = latest.current + 1;
    latest.current = ticket;
    setIsLoading(true);

    try {
      const response = await ancanaApi.get(PATH, {
        params: {
          q: query,
          ...(options?.type ? { type: options.type } : {}),
          ...(options?.rootKey ? { root_key: options.rootKey } : {}),
        },
      });
      if (ticket !== latest.current) return;
      setResult(response.data);
      setTerm(query);
      setError(undefined);
      setIsForbidden(false);
    } catch (e: any) {
      if (ticket !== latest.current) return;
      const status = e?.response?.status;
      // ancana_api answers 503 `status: unavailable` when Pathfinder itself
      // cannot be read — most likely an unset PATHFINDER_TOKEN, since `/v1/*`
      // is authenticated while `/healthz` is not. Surfaced verbatim: it is a
      // deployment fault, and a generic "search failed" would send someone
      // hunting through the wrong system.
      const detail = e?.response?.data?.detail;
      setResult(undefined);
      setTerm(query);
      setIsForbidden(status === 403);
      setError(detail || errorMessage(e, 'The search could not be run.'));
    } finally {
      if (ticket === latest.current) setIsLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    latest.current += 1;
    setResult(undefined);
    setTerm('');
    setError(undefined);
    setIsLoading(false);
  }, []);

  return { result, isLoading, error, isForbidden, term, search, clear };
};

export default useDriveSearch;
